import React, { useEffect, useRef } from 'react';

interface VibeAnimationProps {
  vibe: number; // 0 to 1
  color: string; // hex or rgb for canvas
}

export default function VibeAnimation({ vibe, color }: VibeAnimationProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let particles: Particle[] = [];
    let beams: Beam[] = [];
    
    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
      init();
    };

    class Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      size: number;
      alpha: number;

      constructor(w: number, h: number) {
        this.x = Math.random() * w;
        this.y = Math.random() * h;
        this.vx = (Math.random() - 0.5) * 0.5;
        this.vy = (Math.random() - 0.5) * 0.5;
        this.size = Math.random() * 2 + 1;
        this.alpha = Math.random() * 0.5 + 0.2;
      }

      update(w: number, h: number, speedMult: number) {
        this.x += this.vx * speedMult;
        this.y += this.vy * speedMult;

        if (this.x < 0) this.x = w;
        if (this.x > w) this.x = 0;
        if (this.y < 0) this.y = h;
        if (this.y > h) this.y = 0;
      }

      draw(ctx: CanvasRenderingContext2D, color: string) {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fillStyle = color.replace(')', `, ${this.alpha})`).replace('rgb', 'rgba');
        ctx.fill();
      }
    }

    class Beam {
      angle: number;
      length: number;
      speed: number;
      distance: number;
      width: number;

      constructor(w: number, h: number) {
        this.angle = Math.random() * Math.PI * 2;
        this.distance = Math.random() * 100;
        this.length = Math.random() * 100 + 50;
        this.speed = Math.random() * 15 + 10;
        this.width = Math.random() * 2 + 1;
      }

      update(w: number, h: number) {
        this.distance += this.speed;
        if (this.distance > Math.max(w, h)) {
          this.distance = 0;
          this.angle = Math.random() * Math.PI * 2;
        }
      }

      draw(ctx: CanvasRenderingContext2D, color: string, cx: number, cy: number) {
        const x1 = cx + Math.cos(this.angle) * this.distance;
        const y1 = cy + Math.sin(this.angle) * this.distance;
        const x2 = cx + Math.cos(this.angle) * (this.distance + this.length);
        const y2 = cy + Math.sin(this.angle) * (this.distance + this.length);

        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.strokeStyle = color.replace(')', ', 0.4)').replace('rgb', 'rgba');
        ctx.lineWidth = this.width;
        ctx.lineCap = 'round';
        ctx.stroke();
      }
    }

    const init = () => {
      const w = canvas.width / window.devicePixelRatio;
      const h = canvas.height / window.devicePixelRatio;
      particles = Array.from({ length: 100 }, () => new Particle(w, h));
      beams = Array.from({ length: 40 }, () => new Beam(w, h));
    };

    const render = () => {
      const w = canvas.width / window.devicePixelRatio;
      const h = canvas.height / window.devicePixelRatio;
      const cx = w / 2;
      const cy = h / 2;

      ctx.clearRect(0, 0, w, h);

      if (vibe < 0.7) {
        // Zen / Sparkle Modes
        const speedMult = vibe < 0.3 ? 1 : 4;
        const opacity = vibe < 0.3 ? 0.5 : 0.8;
        
        ctx.globalAlpha = opacity;
        particles.forEach(p => {
          p.update(w, h, speedMult);
          p.draw(ctx, color);
        });
      } else {
        // Warp Mode
        beams.forEach(b => {
          b.update(w, h);
          b.draw(ctx, color, cx, cy);
        });
      }

      animationFrameId = requestAnimationFrame(render);
    };

    window.addEventListener('resize', resize);
    resize();
    render();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, [vibe, color]);

  return (
    <canvas 
      ref={canvasRef} 
      className="absolute inset-0 w-full h-full pointer-events-none opacity-60"
      style={{ filter: vibe > 0.7 ? 'blur(1px)' : 'none' }}
    />
  );
}

